import apiClient from '../axios';

export interface ApiResponse<T> {
  responseValue: T;
  statusCode: number;
  message: string;
}

export interface ContractParameterValue {
  parameterId: number;
  valueIds: number[];
  values: string[];
  type: number;
  value: string;
}

export interface ContractDepartmentUser {
  userId: string;
  userName?: string;
  name?: string;
  surname?: string;
  middlename?: string;
  email?: string;
  position?: string;
  order: number;
  actionSignatureType: number;
}

export interface ContractDepartment {
  departmentId: number;
  departmentName?: string;
  order: number;
  users: ContractDepartmentUser[];
}

export interface ContractListParameter {
  id: number;
  value?: string;
  name?: string;
  order?: number;
  values?: string[];
}

export interface ContractAccounting {
  id: number;
  account?: string;
  isPrimary?: boolean;
  bankId?: number;
  bankName?: string;
  bankVoen?: string;
  bankSwift?: string;
  bankCode?: string;
  bankAddress?: string;
}

export interface ContractServiceRef {
  serviceId: number;
  serviceName?: string;
}

export interface ContractProductRef {
  productId: number;
  productName?: string;
}

export interface ContractCategory {
  id: number;
  name: string;
}

export interface ContractFile {
  id: number;
  path: string;
  name: string;
  type: number;
}

export interface ContractHistoryActor {
  id: string;
  isCompanyUser: boolean;
  name?: string | null;
  surname?: string | null;
  userName?: string | null;
  imageUrl?: string | null;
}

export interface ContractHistoryChange {
  id: number;
  changeType: number;
  section: 'Contract' | 'Document' | 'Step' | 'Timeline' | string;
  entityId?: string | null;
  fieldName: string;
  displayName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
}

export interface ContractHistoryFileChange {
  id?: number;
  changeType: number;
  fileType?: number | null;
  oldFileName?: string | null;
  oldFileUrl?: string | null;
  newFileName?: string | null;
  newFileUrl?: string | null;
  oldFilePath?: string | null;
  newFilePath?: string | null;
  oldContractFileId?: number | null;
  newContractFileId?: number | null;
}

export interface ContractHistoryItem {
  id: number;
  operationId?: string | null;
  occurredAtUtc: string;
  action: string;
  round?: number | null;
  summary?: string | null;
  actor?: ContractHistoryActor | null;
  changes?: ContractHistoryChange[];
  fileChanges?: ContractHistoryFileChange[];
}

export interface GetContractHistoryRequest {
  contractId: number;
  pageNumber?: number;
  pageSize?: number;
  actions?: string[];
}

export interface GetContractHistoryResponse {
  items: ContractHistoryItem[];
  pageNumber: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ContractPartyDto {
  id: number;
  name?: string;
  voen?: string;
  logo?: string;
  address?: string;
  legalAddress?: string;
  phoneNumber?: string | null;
  email?: string | null;
  logoCode?: string;
}

export interface Contract {
  id: number;
  number?: string;
  saveType?: number;
  companyId?: number;
  contractTemplateId?: number;
  contractTemplateCategoryId?: number;
  explanation?: string;
  isRenewable?: boolean;
  isAutoPayment?: boolean;
  paymentDay?: number;
  organizationAccountingId?: number;
  companyAccountingId?: number;
  values?: ContractParameterValue[];
  parameters?: ContractListParameter[];
  departments?: ContractDepartment[];
  files?: ContractFile[];
  sourceFile?: ContractFile | null;
  category?: ContractCategory;
  companyDto?: ContractPartyDto;
  organizationDto?: ContractPartyDto;
  status?: number;
  approvalStatus?: number;
  name?: string;
  code?: string;
  createdDate?: string;
  createdAt?: string;
  updatedAt?: string;
  contractTemplateName?: string;
  categoryName?: string;
  contractTemplate?: {
    id?: number;
    name?: string;
    code?: string;
  };
  isCreatedByCompany?: boolean;
  isTerminationProccessStarted?: boolean;
  contractVersionType?: number;
  contractEmployeesExistingType?: number;
  originalHtml?: string;
  renderedHtml?: string;
  templateId?: number;
  startDate?: string;
  endDate?: string;
  organizationId?: number;
  companyUsers?: FileContractCompanyUser[];
  renewalPeriodValue?: number | null;
  renewalPeriodUnit?: number | null;
  numberSource?: number | null;
  companyNameInDocument?: string | null;
  organizationNameInDocument?: string | null;
  companyOwnerInDocument?: string | null;
  organizationOwnerInDocument?: string | null;
  organizationAccounting?: ContractAccounting;
  companyAccounting?: ContractAccounting;
  services?: ContractServiceRef[];
  products?: ContractProductRef[];
}

export interface CreateContractRequest {
  saveType: number;
  companyId: number;
  contractTemplateId: number;
  values: ContractParameterValue[];
  contractTemplateCategoryId: number;
  explanation: string;
  isRenewable: boolean;
  isAutoPayment: boolean;
  paymentDay: number;
  organizationAccountingId: number;
  companyAccountingId: number;
  departments: ContractDepartment[];
}

export interface UpdateContractRequest {
  id: number;
  saveType: number;
  contractTemplateCategoryId: number;
  explanation: string;
  isRenewable: boolean;
  isAutoPayment: boolean;
  paymentDay: number;
  values: ContractParameterValue[];
  departments: ContractDepartment[];
  organizationAccountingId: number;
  companyAccountingId: number;
}

export interface FileContractDepartmentUser {
  userId: string;
  order: number;
  actionSignatureType: number;
}

export interface FileContractDepartment {
  departmentId: number;
  order: number;
  users: FileContractDepartmentUser[];
}

export interface FileContractCompanyUser {
  companyUserId?: string;
  userId?: string;
  order: number;
  actionSignatureType: number;
  userName?: string;
  fullName?: string;
  name?: string;
  surname?: string;
  middlename?: string;
  fathername?: string;
  email?: string;
  position?: string;
}

export interface FileContractAttachmentInput {
  file: File;
  /** 1 price agreement, 2 additional contract */
  type: 1 | 2;
  totalAmount?: string;
  discount?: string;
}

export interface CreateFileContractRequest {
  saveType: number;
  file: File;
  companyUsers: FileContractCompanyUser[];
  organizationId?: number;
  numberSource?: number;
  attachments?: FileContractAttachmentInput[];
  analysisKey?: string;
}

export interface FileContractAttachmentUpdate {
  id: number;
  file?: File;
  totalAmount?: string;
  discount?: string;
}

export interface UpdateFileContractRequest {
  id: number;
  file?: File;
  companyUsers: FileContractCompanyUser[];
  attachments?: FileContractAttachmentInput[];
  attachmentUpdates?: FileContractAttachmentUpdate[];
  removedAttachmentIds?: number[];
  analysisKey?: string;
}

export type DetectedFileRole =
  | "Contract"
  | "PriceAgreement"
  | "AdditionalContract"
  | "Termination"
  | "Unknown";

export interface DetectedFileContractDocument {
  role?: DetectedFileRole | string | null;
  confidence?: string | null;
  titleQuote?: string | null;
  warnings?: string[] | null;
}

export interface StartVotingFileContractRequest {
  contractId: number;
  companyUsers: FileContractCompanyUser[];
}

export interface ChangeContractStatusRequest {
  contractId: number;
  approvalStatus: number;
}

export interface InitiateSignatureRequest {
  contractId: number;
  redirectUrl: string;
}

export interface InitiateSignatureResponse {
  operationId?: string;
  qrImageBase64?: string;
  qrCode?: string;
}

export interface GetSignatureStatusRequest {
  OperationId: string;
}

export interface GetSignatureStatusResponse {
  statusCode?: number;
  success?: boolean;
  message?: string;
  status?: string | number;
  isSigned?: boolean;
}

export const isSignatureComplete = (status?: GetSignatureStatusResponse | null) => {
  if (!status) return false;
  const value = String(status.status ?? '').toLowerCase();
  return (
    status.success === true ||
    status.isSigned === true ||
    value === 'signed' ||
    value === 'completed' ||
    value === 'success'
  );
};

export interface ContractWebFilters {
  CategoryIds?: number[];
  OrganizationIds?: number[];
  Statuses?: number[];
  ContractVersionType?: number;
  IsRenewable?: boolean;
  StartDateFrom?: string;
  StartDateTo?: string;
  EndDateFrom?: string;
  EndDateTo?: string;
  CreatedDateFrom?: string;
  CreatedDateTo?: string;
}

export interface GetAllContractsRequest extends ContractWebFilters {
  SearchTerm?: string;
}

export interface GetAllContractsWithPaginationRequest extends ContractWebFilters {
  PageSize?: number;
  PageNumber?: number;
  SearchTerm?: string;
  ParameterIds?: number[];
  ShownParameterIds?: number[];
  ApprovalStatus?: number;
}

export interface GetContractTemplateRequest {
  templateId: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export type GetAllContractsResponse = Contract[];
export type GetAllContractsWithPaginationResponse = PaginatedResponse<Contract>;
export type GetAllTerminatedContractsRequest = Omit<
  GetAllContractsWithPaginationRequest,
  'ApprovalStatus'
>;
export type GetAllDraftContractsWithPaginationRequest = GetAllContractsWithPaginationRequest;
export type GetAllCanceledContractsWithPaginationRequest = Omit<
  GetAllContractsWithPaginationRequest,
  'ApprovalStatus'
>;
export type GetAllRevisionContractsWithPaginationRequest =
  GetAllContractsWithPaginationRequest;
export type GetContractByIdResponse = Contract;
export type GetContractTemplateResponse = unknown;

const appendIdList = (params: URLSearchParams, key: string, ids?: number[]) => {
  if (!ids) return;
  for (const id of ids) {
    params.append(key, String(id));
  }
};

const appendContractWebFilters = (params: URLSearchParams, data: ContractWebFilters) => {
  appendIdList(params, 'CategoryIds', data.CategoryIds);
  appendIdList(params, 'OrganizationIds', data.OrganizationIds);
  appendIdList(params, 'Statuses', data.Statuses);

  if (data.ContractVersionType !== undefined) {
    params.append('ContractVersionType', String(data.ContractVersionType));
  }
  if (data.IsRenewable !== undefined) {
    params.append('IsRenewable', data.IsRenewable ? 'true' : 'false');
  }

  const dates: Array<keyof ContractWebFilters> = [
    'StartDateFrom',
    'StartDateTo',
    'EndDateFrom',
    'EndDateTo',
    'CreatedDateFrom',
    'CreatedDateTo',
  ];
  for (const key of dates) {
    const value = data[key];
    if (typeof value === 'string' && value) {
      params.append(key, value);
    }
  }
};

const appendContractListParams = (data: GetAllContractsWithPaginationRequest) => {
  const params = new URLSearchParams();

  if (data.PageSize !== undefined) {
    params.append('PageSize', String(data.PageSize));
  }
  if (data.PageNumber !== undefined) {
    params.append('PageNumber', String(data.PageNumber));
  }
  if (data.SearchTerm) {
    params.append('SearchTerm', data.SearchTerm);
  }
  if (data.ApprovalStatus !== undefined) {
    params.append('ApprovalStatus', String(data.ApprovalStatus));
  }

  appendIdList(params, 'ParameterIds', data.ParameterIds);
  appendIdList(params, 'ShownParameterIds', data.ShownParameterIds);
  appendContractWebFilters(params, data);

  return params;
};

const appendFileContractAttachments = (
  formData: FormData,
  attachments?: FileContractAttachmentInput[]
) => {
  const packed = (attachments || []).filter(
    (item) => item.file && (item.type === 1 || item.type === 2)
  );
  packed.forEach((item, index) => {
    formData.append(`Attachments[${index}].File`, item.file);
    formData.append(`Attachments[${index}].Type`, String(item.type));
    if (item.totalAmount) {
      formData.append(`Attachments[${index}].TotalAmount`, item.totalAmount);
    }
    if (item.discount) {
      formData.append(`Attachments[${index}].Discount`, item.discount);
    }
  });
};

const appendFileContractCompanyUsers = (
  formData: FormData,
  companyUsers: FileContractCompanyUser[]
) => {
  companyUsers.forEach((user, index) => {
    formData.append(
      `CompanyUsers[${index}].CompanyUserId`,
      user.companyUserId || user.userId || ""
    );
    formData.append(`CompanyUsers[${index}].Order`, String(user.order));
    formData.append(
      `CompanyUsers[${index}].ActionSignatureType`,
      String(user.actionSignatureType)
    );
  });
};

export const contractService = {
  async createContract(data: CreateContractRequest): Promise<ApiResponse<Contract>> {
    const response = await apiClient.post('/Contracts/web/create', data);
    return response.data;
  },

  async updateContract(data: UpdateContractRequest): Promise<ApiResponse<Contract>> {
    const response = await apiClient.put('/Contracts/web/update', data);
    return response.data;
  },

  async detectFileContractDocument(
    file: File
  ): Promise<DetectedFileContractDocument | null> {
    const formData = new FormData();
    formData.append("File", file);
    const response = await apiClient.post(
      "/Contracts/web/detect-file-contract-document",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data?.responseValue ?? null;
  },

  async createFileContract(
    data: CreateFileContractRequest
  ): Promise<ApiResponse<Contract>> {
    const formData = new FormData();
    formData.append('SaveType', String(data.saveType));
    formData.append('File', data.file);
    if (data.organizationId) {
      formData.append('OrganizationId', String(data.organizationId));
    }
    if (data.numberSource) {
      formData.append('NumberSource', String(data.numberSource));
    }
    appendFileContractAttachments(formData, data.attachments);
    if (data.analysisKey) {
      formData.append("AnalysisKey", data.analysisKey);
    }
    appendFileContractCompanyUsers(formData, data.companyUsers);

    const response = await apiClient.post('/Contracts/web/create-file-contract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 200_000,
    });
    return response.data;
  },

  async updateFileContract(
    data: UpdateFileContractRequest
  ): Promise<ApiResponse<Contract>> {
    const formData = new FormData();
    formData.append('Id', String(data.id));
    if (data.file) {
      formData.append('File', data.file);
    }
    if (data.analysisKey) {
      formData.append("AnalysisKey", data.analysisKey);
    }
    appendFileContractAttachments(formData, data.attachments);
    (data.attachmentUpdates || [])
      .filter((item) => Number.isFinite(item.id))
      .forEach((item, index) => {
      formData.append(`AttachmentUpdates[${index}].Id`, String(item.id));
      if (item.file) {
        formData.append(`AttachmentUpdates[${index}].File`, item.file);
      }
      if (item.totalAmount) {
        formData.append(`AttachmentUpdates[${index}].TotalAmount`, item.totalAmount);
      }
      if (item.discount) {
        formData.append(`AttachmentUpdates[${index}].Discount`, item.discount);
      }
    });
    (data.removedAttachmentIds || [])
      .filter((id) => Number.isFinite(id))
      .forEach((id, index) => {
      formData.append(`RemovedAttachmentIds[${index}]`, String(id));
    });
    appendFileContractCompanyUsers(formData, data.companyUsers);

    const response = await apiClient.put('/Contracts/web/update-file-contract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 200_000,
    });
    return response.data;
  },

  async startVotingFileContract(
    data: StartVotingFileContractRequest
  ): Promise<ApiResponse<null>> {
    const response = await apiClient.post('/Contracts/web/start-voting-file-contract', data);
    return response.data;
  },

  async deleteFileContract(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete('/Contracts/web/delete-file-contract', {
      params: { Id: id },
    });
    return response.data;
  },

  async deleteContract(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete(`/Contracts/web/delete/${id}`);
    return response.data;
  },

  async changeStatus(data: ChangeContractStatusRequest): Promise<ApiResponse<null>> {
    const response = await apiClient.patch('/Contracts/web/change-status', data);
    return response.data;
  },

  async initiateSignature(
    data: InitiateSignatureRequest
  ): Promise<ApiResponse<InitiateSignatureResponse>> {
    const response = await apiClient.post('/Contracts/web/initiate-signature', data);
    return response.data;
  },

  async getSignatureStatus(
    data: GetSignatureStatusRequest
  ): Promise<ApiResponse<GetSignatureStatusResponse>> {
    const params = new URLSearchParams();
    params.append('OperationId', data.OperationId);

    const response = await apiClient.get('/Contracts/web/signature-status', { params });
    return response.data;
  },

  async getAllContracts(
    data: GetAllContractsRequest = {}
  ): Promise<ApiResponse<GetAllContractsResponse>> {
    const params = new URLSearchParams();

    if (data.SearchTerm) {
      params.append('SearchTerm', data.SearchTerm);
    }
    appendContractWebFilters(params, data);

    const response = await apiClient.get('/Contracts/web/get-all', { params });
    return response.data;
  },

  async getContractById(id: number): Promise<ApiResponse<GetContractByIdResponse>> {
    const response = await apiClient.get(`/Contracts/web/get-by-id/${id}`);
    return response.data;
  },

  async getContractHistory(
    data: GetContractHistoryRequest
  ): Promise<ApiResponse<GetContractHistoryResponse>> {
    const params = new URLSearchParams();
    params.append('ContractId', String(data.contractId));
    params.append('PageNumber', String(data.pageNumber ?? 1));
    params.append('PageSize', String(data.pageSize ?? 50));
    if (data.actions?.length) {
      params.append('Actions', data.actions.join(','));
    }

    const response = await apiClient.get('/Contracts/web/get-history', { params });
    return response.data;
  },

  async getAllContractsWithPagination(
    data: GetAllContractsWithPaginationRequest = {}
  ): Promise<ApiResponse<GetAllContractsWithPaginationResponse>> {
    const response = await apiClient.get('/Contracts/web/get-all-with-pagination', {
      params: appendContractListParams(data),
    });
    return response.data;
  },

  async getAllTerminated(
    data: GetAllTerminatedContractsRequest = {}
  ): Promise<ApiResponse<GetAllContractsWithPaginationResponse>> {
    const response = await apiClient.get('/Contracts/web/get-all-terminated', {
      params: appendContractListParams(data),
    });
    return response.data;
  },

  async getAllDraftsWithPagination(
    data: GetAllDraftContractsWithPaginationRequest = {}
  ): Promise<ApiResponse<GetAllContractsWithPaginationResponse>> {
    const response = await apiClient.get('/Contracts/web/get-all-drafts-with-pagination', {
      params: appendContractListParams(data),
    });
    return response.data;
  },

  async getAllCanceledWithPagination(
    data: GetAllCanceledContractsWithPaginationRequest = {}
  ): Promise<ApiResponse<GetAllContractsWithPaginationResponse>> {
    const response = await apiClient.get('/Contracts/web/get-all-canceled-with-pagination', {
      params: appendContractListParams(data),
    });
    return response.data;
  },

  async getAllRevisionWithPagination(
    data: GetAllRevisionContractsWithPaginationRequest = {}
  ): Promise<ApiResponse<GetAllContractsWithPaginationResponse>> {
    const response = await apiClient.get(
      '/Contracts/web/get-all-revision-with-pagination',
      { params: appendContractListParams(data) }
    );
    return response.data;
  },

  async getTemplate(
    data: GetContractTemplateRequest
  ): Promise<ApiResponse<GetContractTemplateResponse>> {
    const response = await apiClient.get(`/Contracts/web/get-template/${data.templateId}`);
    return response.data;
  },
};

export class RenderService {
  static renderTemplate(template: string, data: Record<string, any>): string {
    let rendered = template;

    Object.entries(data).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');

      let replacement = '';
      if (value && value.toString().trim()) {
        replacement = `<span class="dynamic-field-value">${value}</span>`;
      } else {
        replacement = `<span class="dynamic-field-empty">{{${key}}}</span>`;
      }

      rendered = rendered.replace(regex, replacement);
    });

    rendered = rendered.replace(/\{\{([^}]+)\}\}/g, '<span class="dynamic-field-placeholder">{{$1}}</span>');

    return rendered;
  }

  static async generatePDF(contractId: string, _html: string): Promise<string> {
    return `/contracts/pdf/${contractId}.pdf`;
  }

  static sanitizeHtml(html: string): string {
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/javascript:/gi, '');
  }

  static extractFieldsFromTemplate(template: string): string[] {
    const fieldRegex = /\{\{([^}]+)\}\}/g;
    const fields: string[] = [];
    let match;

    while ((match = fieldRegex.exec(template)) !== null) {
      if (!fields.includes(match[1])) {
        fields.push(match[1]);
      }
    }

    return fields;
  }

  static validateTemplate(template: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const openBraces = (template.match(/\{\{/g) || []).length;
    const closeBraces = (template.match(/\}\}/g) || []).length;

    if (openBraces !== closeBraces) {
      errors.push('Unmatched template braces');
    }

    if (template.includes('{{{') || template.includes('}}}')) {
      errors.push('Nested braces are not supported');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export default contractService;
